/**
 * FleetManager Mobile PWA - Main App Component
 * Handles routing and authentication for mobile interface
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import MobileLayout from './MobileLayout';
import LoginView from './views/LoginView';
import VehiclesView from './views/VehiclesView';
import VehicleDetailView from './views/VehicleDetailView';
import TodosView from './views/TodosView';
import AlertsView from './views/AlertsView';
import ProfileView from './views/ProfileView';
import { mobileAPI } from '../api';

// Mobile Auth Context
const MobileAuthContext = createContext(null);

export function useMobileAuth() {
  return useContext(MobileAuthContext);
}

// Simple hash-based router for mobile
function useHashRouter() {
  const [route, setRoute] = useState(window.location.hash.slice(1) || '/vehicles');

  useEffect(() => {
    const handleHashChange = () => {
      setRoute(window.location.hash.slice(1) || '/vehicles');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = (path) => {
    window.location.hash = path;
  };

  return { route, navigate };
}

export default function MobileApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { route, navigate } = useHashRouter();

  // Check for existing session
  useEffect(() => {
    const token = localStorage.getItem('fm_mobile_access_token');
    const userData = localStorage.getItem('fm_mobile_user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch {
        localStorage.removeItem('fm_mobile_access_token');
        localStorage.removeItem('fm_mobile_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const result = await mobileAPI.login(email, password);
    // API returns { access_token, refresh_token, user } on success
    // or { error } on failure
    if (result.access_token) {
      localStorage.setItem('fm_mobile_access_token', result.access_token);
      localStorage.setItem('fm_mobile_refresh_token', result.refresh_token);
      localStorage.setItem('fm_mobile_user', JSON.stringify(result.user));
      setUser(result.user);
      navigate('/vehicles');
      return { ok: true };
    }
    return { ok: false, error: result.error || 'Identifiants invalides' };
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('fm_mobile_refresh_token');
    if (refreshToken) {
      await mobileAPI.logout(refreshToken);
    }
    localStorage.removeItem('fm_mobile_access_token');
    localStorage.removeItem('fm_mobile_refresh_token');
    localStorage.removeItem('fm_mobile_user');
    setUser(null);
    navigate('/login');
  };

  const getToken = () => localStorage.getItem('fm_mobile_access_token');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!user) {
    return (
      <MobileAuthContext.Provider value={{ user, login, logout, getToken }}>
        <LoginView onLogin={login} />
      </MobileAuthContext.Provider>
    );
  }

  // Parse route
  const routeParts = route.split('/').filter(Boolean);
  const mainRoute = routeParts[0] || 'vehicles';
  const subRoute = routeParts[1];

  // Render current view
  const renderView = () => {
    switch (mainRoute) {
      case 'vehicles':
        if (subRoute) {
          return <VehicleDetailView vehicleId={parseInt(subRoute)} onBack={() => navigate('/vehicles')} />;
        }
        return <VehiclesView onSelect={(id) => navigate(`/vehicles/${id}`)} />;
      case 'todos':
        return <TodosView />;
      case 'alerts':
        return <AlertsView />;
      case 'profile':
        return <ProfileView onLogout={logout} />;
      default:
        return <VehiclesView onSelect={(id) => navigate(`/vehicles/${id}`)} />;
    }
  };

  return (
    <MobileAuthContext.Provider value={{ user, login, logout, getToken }}>
      <MobileLayout currentTab={mainRoute} onTabChange={navigate}>
        {renderView()}
      </MobileLayout>
    </MobileAuthContext.Provider>
  );
}
